import { Dispatch } from '@reduxjs/toolkit';
import { injectReducer } from 'ducks/configureStore';
import {
  Feature,
  RenderLocation,
  RendersComponent,
  ShouldReset
} from '../../../../features/Feature';
import { AutoFillDoorTypeEnum } from '@inter-ikea-storageone/range-pax/src/api/components/AutofillSingleWallComponent';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'ducks';
import { AutofillPaxUi } from './AutofillPaxUi';
import { ProductDesign } from '@inter-ikea-storageone/range';

export type AutofillState = {
  autofillActive: boolean;
  frameLayoutChoice: number;
  doorTypeChoice: AutoFillDoorTypeEnum;
  singleWallWidth: number;
  cornerWallLeftWidth: number;
  cornerWallRightWidth: number;
  autoFillSheetOpen: boolean;
  autoFillMobileFramesSheetOpen: boolean;
  standardFramesSheetOpen: boolean;
  currentColor: ProductDesign;
  singleWallInfoSheetOpen: boolean;
  cornerWallInfoSheetOpen: boolean;
};

const initialState: AutofillState = {
  autofillActive: false,
  frameLayoutChoice: 0,
  doorTypeChoice: AutoFillDoorTypeEnum.HINGED,
  singleWallWidth: 1500,
  cornerWallLeftWidth: 1110,
  cornerWallRightWidth: 1110,
  autoFillSheetOpen: false,
  autoFillMobileFramesSheetOpen: false,
  standardFramesSheetOpen: false,
  currentColor: ProductDesign.WHITE,
  singleWallInfoSheetOpen: false,
  cornerWallInfoSheetOpen: false
};

type ExtendedRootState = RootState & {
  autofill: AutofillState;
};

const AutofillPaxSlice = createSlice({
  name: 'AutofillPax',
  initialState,
  reducers: {
    setAutofillActive(state, action: PayloadAction<boolean>) {
      state.autofillActive = action.payload;
    },
    setAutoFillFrameLayout(state, action: PayloadAction<number>) {
      state.frameLayoutChoice = action.payload;
    },
    setAutoFillDoorType(state, action: PayloadAction<AutoFillDoorTypeEnum>) {
      state.doorTypeChoice = action.payload;
    },
    setSingleWallWidth(state, action: PayloadAction<number>) {
      state.singleWallWidth = action.payload;
    },
    setCornerWallLeftWidth(state, action: PayloadAction<number>) {
      state.cornerWallLeftWidth = action.payload;
    },
    setCornerWallRightWidth(state, action: PayloadAction<number>) {
      state.cornerWallRightWidth = action.payload;
    },

    setAutoFillSheetVisible(state, action: PayloadAction<boolean>) {
      state.autoFillSheetOpen = action.payload;
    },
    setAutoFillMobileFramesSheetVisible(state, action: PayloadAction<boolean>) {
      state.autoFillMobileFramesSheetOpen = action.payload;
    },
    setStandardFramesSheetVisible(state, action: PayloadAction<boolean>) {
      state.standardFramesSheetOpen = action.payload;
    },
    setCurrentColor(state, action: PayloadAction<ProductDesign>) {
      state.currentColor = action.payload;
    },
    setSingleWallInfoSheetVisible(state, action: PayloadAction<boolean>) {
      state.singleWallInfoSheetOpen = action.payload;
    },
    setCornerWallInfoSheetVisible(state, action: PayloadAction<boolean>) {
      state.cornerWallInfoSheetOpen = action.payload;
    }
  }
});

export const isAutofillActive = (state: ExtendedRootState) => state.autofill?.autofillActive;
export const autofillFrameLayout = (state: ExtendedRootState) => state.autofill.frameLayoutChoice;
export const autofillDoorType = (state: ExtendedRootState) => state.autofill.doorTypeChoice;
export const singleWallWidth = (state: ExtendedRootState) => state.autofill.singleWallWidth;
export const cornerWallLeftWidth = (state: ExtendedRootState) => state.autofill.cornerWallLeftWidth;
export const cornerWallRightWidth = (state: ExtendedRootState) =>
  state.autofill.cornerWallRightWidth;
export const isAutoFillSheetVisible = (state: ExtendedRootState) =>
  state?.autofill?.autoFillSheetOpen;
export const isAutoFillMobileFramesSheetVisible = (state: ExtendedRootState) =>
  state.autofill.autoFillMobileFramesSheetOpen;
export const isStandardFramesSheetVisible = (state: ExtendedRootState) =>
  state.autofill.standardFramesSheetOpen;
export const currentColor = (state: ExtendedRootState) => state.autofill.currentColor;
export const isSingleWallInfoSheetVisible = (state: ExtendedRootState) =>
  state.autofill.singleWallInfoSheetOpen;
export const isCornerWallInfoSheetVisible = (state: ExtendedRootState) =>
  state.autofill.cornerWallInfoSheetOpen;

export const {
  setAutofillActive,
  setAutoFillFrameLayout,
  setAutoFillDoorType,
  setSingleWallWidth,
  setCornerWallLeftWidth,
  setCornerWallRightWidth,
  setAutoFillSheetVisible,
  setAutoFillMobileFramesSheetVisible,
  setStandardFramesSheetVisible,
  setCurrentColor,
  setSingleWallInfoSheetVisible,
  setCornerWallInfoSheetVisible
} = AutofillPaxSlice.actions;

export class AutofillPax extends Feature implements RendersComponent, ShouldReset {
  protected init(): void {
    injectReducer<AutofillState>('autofill', AutofillPaxSlice.reducer);
  }
  render(location: RenderLocation) {
    if (location !== RenderLocation.Root) return;
    return AutofillPaxUi;
  }
  reset(dispatch: Dispatch) {
    dispatch(setAutofillActive(false));
    dispatch(setAutoFillSheetVisible(false));
    dispatch(setStandardFramesSheetVisible(false));
    dispatch(setCurrentColor(ProductDesign.WHITE));
    dispatch(setAutoFillFrameLayout(0));
    dispatch(setSingleWallInfoSheetVisible(false));
    dispatch(setCornerWallInfoSheetVisible(false));
  }
}
